import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /**
   * Holds inactive collection representations in detached fragments so only one view remains
   * connected and consuming layout, component, and event resources.
   * @type {WeakMap<Element, {card: DocumentFragment, catalog: DocumentFragment}>}
   */
  #productViewFragments = new WeakMap();

  /** @type {MutationObserver | null} */
  #productItemObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#restoreStoredLayout();
    this.#syncProductViews(this.refs.grid?.getAttribute('product-grid-view') || 'default');
    this.#observeProductItems();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#productItemObserver?.disconnect();
    this.#productItemObserver = null;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    this.#persistLayout(value);
    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
  };

  /**
   * Restores the last chosen collection/search layout after navigation.
   */
  #restoreStoredLayout() {
    const { grid } = this.refs;
    if (!grid) return;

    const pageDefault = grid.dataset.defaultView || 'default';
    const storedLayoutMobile = sessionStorage.getItem('product-grid-view-mobile');
    const storedLayoutDesktop = sessionStorage.getItem('product-grid-view-desktop');

    if (storedLayoutMobile === null && storedLayoutDesktop === null) return;

    const isDesktop = mediaQueryLarge.matches;
    let storedLayout = (isDesktop ? storedLayoutDesktop : storedLayoutMobile) || pageDefault;
    const optionName = isDesktop ? 'grid' : 'grid-mobile';
    const options = this.querySelectorAll(`input[type="radio"][name="${optionName}"]`);
    const supportsStoredLayout = Array.from(options).some((option) => option.value === storedLayout);

    if (!supportsStoredLayout) storedLayout = pageDefault;

    this.#setLayout(storedLayout);

    for (const option of options) {
      option.checked = option.value === storedLayout;
    }
  }

  /**
   * Saves the chosen layout so collection pages keep it while browsing.
   *
   * @param {string} value
   */
  #persistLayout(value) {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`product-grid-view-${viewport}`, value);

    if (value === 'catalog') {
      sessionStorage.setItem('product-grid-view-desktop', 'catalog');
      sessionStorage.setItem('product-grid-view-mobile', 'catalog');
      return;
    }

    const otherViewport = viewport === 'desktop' ? 'mobile' : 'desktop';
    if (sessionStorage.getItem(`product-grid-view-${otherViewport}`) === 'catalog') {
      sessionStorage.setItem(`product-grid-view-${otherViewport}`, 'default');
    }
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductViews(value);
  }

  /**
   * Keeps only the selected product representation connected on collection pages.
   * Search pages have no lazy product-view templates and remain unchanged.
   * @param {string} value
   */
  #syncProductViews(value) {
    if (Shopify.designMode) return;

    const { grid } = this.refs;
    if (!grid) return;

    const view = value === 'catalog' ? 'catalog' : 'card';
    for (const item of grid.querySelectorAll(':scope > .product-grid__item')) {
      this.#syncProductItem(item, view);
    }
  }

  /**
   * Moves one product's inactive representation into a detached document fragment.
   * @param {Element} item
   * @param {'card' | 'catalog'} view
   */
  #syncProductItem(item, view) {
    let fragments = this.#productViewFragments.get(item);
    if (!fragments) {
      const cardTemplate = item.querySelector(':scope > [data-ib-product-card-template]');
      const catalog = item.querySelector(':scope > [data-ib-product-view="catalog"]');
      if (!(cardTemplate instanceof HTMLTemplateElement) || !(catalog instanceof HTMLElement)) return;

      const card = document.createDocumentFragment();
      card.append(...cardTemplate.content.childNodes);
      fragments = {
        card,
        catalog: document.createDocumentFragment(),
      };
      this.#productViewFragments.set(item, fragments);
      cardTemplate.remove();
    }

    const activeView = item.querySelector(':scope > [data-ib-product-view]');
    const activeName = activeView?.getAttribute('data-ib-product-view');
    if (activeName === view) return;

    if (activeView && (activeName === 'card' || activeName === 'catalog')) {
      fragments[activeName].appendChild(activeView);
    }
    item.appendChild(fragments[view]);
  }

  /**
   * Applies the current view to products appended by infinite scrolling.
   */
  #observeProductItems() {
    this.#productItemObserver?.disconnect();

    const { grid } = this.refs;
    if (!grid || Shopify.designMode) return;

    this.#productItemObserver = new MutationObserver((mutations) => {
      const view = grid.getAttribute('product-grid-view') === 'catalog' ? 'catalog' : 'card';
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element && node.matches('.product-grid__item')) {
            this.#syncProductItem(node, view);
          }
        }
      }
    });
    this.#productItemObserver.observe(grid, { childList: true });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const currentLayout = this.refs.grid?.getAttribute('product-grid-view');
    if (currentLayout === 'catalog') {
      const catalogOption = event.matches
        ? this.querySelector('input[name="grid"][value="catalog"]')
        : this.querySelector('input[name="grid-mobile"][value="catalog"]');

      if (catalogOption instanceof HTMLInputElement) {
        catalogOption.checked = true;
        this.#setLayout('catalog');
        return;
      }
    }

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
